import { Slider } from '@tunein/react-ui';
import classNames from 'clsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { player } from 'src/client/player';
import Popover from 'src/common/components/shared/Popover';
import VolumeControlIcon from 'src/common/components/shared/svgIcons/player/VolumeControlIcon';
import VolumeControlOffIcon from 'src/common/components/shared/svgIcons/player/VolumeControlOffIcon';
import withOutsideClickControl from 'src/common/decorators/withOutsideClickControl';
import { selectIsMobile } from 'src/common/selectors/app';
import { useStores } from 'src/common/stores';
import cssVars from 'src/common/styles/variables';
import { isInDiscordIFrame } from '../../../../utils/discord';
import css from './volume-control.module.scss';

const defaultIconProps = {
  width: cssVars['--large-icon-size'],
  height: cssVars['--large-icon-size'],
};

const maxVolume = 100;
const maxVolumeInDiscordIFrame = 25;

function VolumeControl({
  volumeSliderClass,
  popoverContainerClass,
  volumeContainerClass,
  iconContainerClass,
  iconProps,
  isDisabled,

  // via withOutsideClickControl
  containerRef,
  handleOnClick,
  isOpen,
}) {
  const { playerStore } = useStores();
  const playerManagerVolume = playerStore.volume();
  const showVolumeBar = playerStore.showVolumeBar();

  // Covert value for Slider component to be between 0 and 100
  const volume = playerManagerVolume * maxVolume;
  const muted = !volume;

  function handleSliderChange(value) {
    // Covert value from Slider component to be between 0 and 1
    const nextValue = Array.isArray(value) ? value[0] : value;
    const newVolume = nextValue / maxVolume;
    player.setVolume(newVolume);
  }

  const VolumeIconComponent =
    volume && !muted && !isDisabled ? VolumeControlOffIcon : VolumeControlIcon;
  const isMobile = useSelector(selectIsMobile);
  const sliderMax = isInDiscordIFrame() ? maxVolumeInDiscordIFrame : maxVolume;

  const playerVolumeClick = () => {
    if (!isDisabled && isMobile) {
      player.mute(!player.muted);
      return;
    }
    if (!isDisabled) {
      handleOnClick();
    }
  };

  return (
    <div id="playerVolumeContainer" className={volumeContainerClass} ref={containerRef}>
      <div
        id="playerVolume"
        data-testid="playerVolume"
        data-volume={volume}
        className={classNames(iconContainerClass, {
          [css.disabledVolume]: isDisabled,
        })}
        role="button"
        tabIndex={0}
        onClick={playerVolumeClick}
        onKeyDown={playerVolumeClick}
      >
        <VolumeIconComponent className={css.overflowVisible} {...defaultIconProps} {...iconProps} />
      </div>
      <Popover
        id="playerVolumePopover"
        isOpen={isOpen || showVolumeBar}
        className={classNames(css.popover, popoverContainerClass)}
      >
        <div className={css.content}>
          <div className={css.sliderShell}>
            <Slider
              id="playerVolumeSlider"
              data-testid="playerVolumeSlider"
              className={classNames(css.volumeSlider, volumeSliderClass)}
              controlClassName={css.volumeSliderControl}
              trackClassName={css.volumeSliderTrack}
              rangeClassName={css.volumeSliderRange}
              thumbClassName={css.volumeSliderThumb}
              value={Math.min(volume, sliderMax)}
              onValueChange={handleSliderChange}
              max={sliderMax}
              orientation="vertical"
              disabled={isDisabled}
            />
          </div>
        </div>
      </Popover>
    </div>
  );
}

VolumeControl.propTypes = {
  volumeSliderClass: PropTypes.string,
  popoverContainerClass: PropTypes.string,
  volumeContainerClass: PropTypes.string,
  iconContainerClass: PropTypes.string,
  iconProps: PropTypes.object,
  isDisabled: PropTypes.bool,

  // via withOutsideClickControl
  containerRef: PropTypes.object.isRequired,
  handleOnClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

export default withOutsideClickControl(VolumeControl, true);
